import { GetStaticProps, GetStaticPaths } from 'next'
import { getClient } from '../../lib/sanity.client'
import {
  blogBySlugQuery,
  blogListQuery,
  getNavigation,
} from '../../lib/sanity.queries'
import { urlForImage } from '../../lib/sanity.image'
import Image from 'next/image'
import { PortableText, PortableTextComponentProps } from '@portabletext/react'
import { format } from 'date-fns'
import { readToken } from '~/lib/sanity.api'
import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import Layout from '~/components/Layout'
import Head from 'next/head'
import { PAGE_TITLE_PREFIX } from '~/components/constants'
import Link from 'next/link'
import { Column } from '~/components/Grid'
import Card from '~/components/Card'
import Eyebrow from '~/components/Eyebrow'
import { TwitterIcon } from '~/components/icons/TwitterIcon'
import { EmailIcon } from '~/components/icons/EmailIcon'
import { FacebookIcon, LinkedinIcon } from 'lucide-react'
import { TelegramIcon } from '~/components/icons/TelegramIcon'

interface BlogPost {
  _id: string
  title: string
  slug: { current: string }
  mainImage?: {
    asset: {
      url: string
      _type: string
      _ref: string
    }
    alt?: string
  }
  excerpt: string
  publishedAt: string
  category?: string
  content: any[]
  author?: {
    name: string
  }
  minutesToRead: number
}

interface Props {
  post: BlogPost
  draftMode: boolean
  token: string
  navigation: any
  relatedPosts: BlogPost[]
}

const components = {
  block: {
    h1: ({ children }: PortableTextComponentProps<any>) => (
      <h1 className="h1 mt-8 mb-4">{children}</h1>
    ),
    h2: ({ children }: PortableTextComponentProps<any>) => (
      <h2 className="h2 mt-6 mb-3">{children}</h2>
    ),
    h3: ({ children }: PortableTextComponentProps<any>) => (
      <h3 className="h3 mt-5 mb-2">{children}</h3>
    ),
    h4: ({ children }: PortableTextComponentProps<any>) => (
      <h4 className="h4 mt-4 mb-1">{children}</h4>
    ),
    h5: ({ children }: PortableTextComponentProps<any>) => (
      <h5 className="h5 mt-6 mb-4">{children}</h5>
    ),
    normal: ({ children }: PortableTextComponentProps<any>) => (
      <p className="">{children}</p>
    ),
    ul: ({ children }: PortableTextComponentProps<any>) => (
      <ul className="list-disc list-inside mt-6">{children}</ul>
    ),
  },
  marks: {
    link: ({ children, value }: any) => (
      <a
        href={value.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 underline hover:text-blue-700"
      >
        {children}
      </a>
    ),
  },
  types: {
    image: ({ value }: { value: any }) => (
      <motion.div
        className="relative w-full h-96 my-8 overflow-hidden rounded-2xl"
        initial={{ scale: 0.95, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <Image
          src={urlForImage(value).url()}
          alt={value.alt || ''}
          fill
          className="object-cover transition-transform duration-300 hover:scale-105"
        />
      </motion.div>
    ),
    code: ({ value }: { value: { code: string; language?: string } }) => (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <pre className="bg-gray-900 text-white p-4 rounded-lg my-4 overflow-x-auto">
          <code className="font-mono text-sm">{value.code}</code>
        </pre>
      </motion.div>
    ),
  },
}

export default function BlogPost({
  post,
  draftMode,
  token,
  navigation,
  relatedPosts,
}: Props) {
  const headerRef = useRef(null)
  const isInView = useInView(headerRef, { once: true })
  const pageTitle = `${PAGE_TITLE_PREFIX} ${post.title}`

  const topics = [
    'Developers',
    'Case Studies',
    'Partners',
    'News',
    'Product',
    'AI & ML',
    'Testnets',
    'Community',
    'Insights',
    'Team',
    'Updates',
  ]

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <Layout navigation={navigation}>
        <article className="relative min-h-screen pt-36 md:pt-44">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.header
              ref={headerRef}
              className="mb-12 text-center"
              initial="initial"
              animate={isInView ? 'animate' : 'initial'}
              variants={{
                initial: { opacity: 0 },
                animate: { opacity: 1, transition: { staggerChildren: 0.1 } },
              }}
            >
              <motion.h1 className="h1 text-gradient text-pretty mb-4">
                {post.title}
              </motion.h1>

              <div className="flex flex-wrap items-center justify-center gap-6 md:gap-3">
                {post.category && (
                  <motion.span className="rounded-full bg-green-100 text-green-800 text-sm font-medium hidden md:inline-block">
                    {post.category}
                  </motion.span>
                )}
                <motion.span className="text-gray-500 font-medium hidden md:inline">•</motion.span>
                <motion.time
                  className="text-gray-500 font-medium"
                  dateTime={post.publishedAt}
                >
                  {format(new Date(post.publishedAt), 'MMMM d, yyyy')}
                </motion.time>
                <motion.span className="text-gray-500 font-medium hidden md:inline">•</motion.span>
                {post.minutesToRead && (
                  <motion.span className="text-gray-500 font-medium">
                    {Math.ceil(post.minutesToRead)} min read
                  </motion.span>
                )}
              </div>
            </motion.header>

            {post.mainImage && (
              <motion.div
                className="relative w-full h-[60vh] mb-12 rounded-2xl overflow-hidden"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <Image
                  src={post.mainImage.asset.url}
                  alt={post.title}
                  fill
                  priority
                  className="object-cover"
                />
              </motion.div>
            )}

            <motion.div
              style={{ lineHeight: '1.6', marginBottom: '1rem' }}
              className="prose prose-lg max-w-none prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-xl prose-h4:text-lg prose-h5:text-md prose-h6:text-sm prose-p:mb-6 prose-headings:mt-12 prose-headings:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <PortableText value={post.content} components={components} />
            </motion.div>

            <motion.div
              className="mt-16 pt-8 border-t border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-semibold mb-6">Connect with us</h2>
              <div className="flex gap-4 items-center">
                <Link
                  href={`https://twitter.com/NodeAIETH`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-6 transition duration-300 hover:opacity-50"
                  aria-label="Share on Twitter"
                >
                  <TwitterIcon className="h-5 w-5" />
                </Link>
                <Link
                  href={`https://t.me/nodeaieth`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-6 transition duration-300 hover:opacity-50"
                  aria-label="Share on Telegram"
                >
                  <TelegramIcon className="h-6 w-6" />
                </Link>
                {/* <Link
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                    typeof window !== 'undefined' ? window.location.href : '',
                  )}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-6 transition duration-300 hover:opacity-50"
                  aria-label="Share on LinkedIn"
                >
                  <LinkedinIcon className="h-6 w-6" />
                </Link> */}
                <Link
                  href={`mailto:hello@nodes.ai`}
                  className="h-6 transition duration-300 hover:opacity-50"
                  aria-label="Share via Email"
                >
                  <EmailIcon className="h-6 w-6" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              className="mt-16 pt-8 border-t border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-semibold mb-6">Explore Topics</h2>
              <div className="flex flex-wrap gap-2 mb-12">
                {topics.map((topic) => (
                  <Link
                    key={topic}
                    href="/blog"
                    className="px-3 py-1 bg-grey-400 hover:bg-grey-300 rounded-full text-sm text-white transition-colors"
                  >
                    {topic}
                  </Link>
                ))}
              </div>

              <h2 className="text-2xl font-semibold mb-6">Related Articles</h2>
              <div className="grid gap-6 mb-12">
                {relatedPosts?.slice(0, 3).map((post) => (
                  <Column
                    key={post._id}
                    className="col-span-12 md:col-span-6 lg:col-span-4 mb-8"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Link href={`/blog/${post.slug.current}`}>
                        <Card className="h-full hover:scale-[1.02] transition-transform duration-300">
                          {post.mainImage && (
                            <div className="relative h-48 mb-6 rounded-lg overflow-hidden">
                              <Image
                                src={urlForImage(post.mainImage).url()}
                                alt={post.title}
                                fill
                                className="object-cover rounded-xl"
                              />
                            </div>
                          )}
                          <Eyebrow className="max-w-fit">
                            {post.category}
                          </Eyebrow>
                          <h2 className="h5 text-lg text-gradient my-4">
                            {post.title}
                          </h2>
                          <p className="text-grey mb-6">{post.excerpt}</p>
                          <time className="mono-small text-grey">
                            {format(new Date(post.publishedAt), 'MMMM d, yyyy')}
                          </time>
                        </Card>
                      </Link>
                    </motion.div>
                  </Column>
                ))}
              </div>
            </motion.div>
          </div>
        </article>
      </Layout>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({
  params,
  draftMode = false,
}) => {
  if (!params?.slug) {
    return { notFound: true }
  }

  try {
    const client = getClient(draftMode ? { token: readToken } : undefined)
    const [post, navigation, relatedPosts] = await Promise.all([
      client.fetch(blogBySlugQuery, { slug: params.slug }),
      getNavigation(client),
      client.fetch(blogListQuery),
    ])

    if (!post) {
      return { notFound: true }
    }

    return {
      props: {
        post,
        draftMode,
        navigation,
        token: draftMode ? readToken : '',
        relatedPosts: relatedPosts
          .filter((p: BlogPost) => p.slug.current !== params.slug)
          .slice(0, 3),
      },
      revalidate: 60,
    }
  } catch (error) {
    console.error('Error fetching blog post:', error)
    return { notFound: true }
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await getClient({ token: readToken }).fetch(blogListQuery)

  return {
    paths: posts.map((post: BlogPost) => ({
      params: { slug: post.slug.current },
    })),
    fallback: 'blocking',
  }
}
